import { useState, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { API_BASE_URL } from "@/api/config";
import type { ProgressEvent, ProgressEventType } from "@markeroo/shared";
import { ProgressEventSchema } from "@markeroo/shared";

interface UseProgressStreamOptions {
  assignmentId: string;
  documentId: string;
  enabled?: boolean;
  onProgress?: (event: ProgressEvent) => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

interface ProgressStreamState {
  isConnected: boolean;
  wordsGenerated: number;
  stage: string | null;
  eventType: ProgressEventType | null;
  error: Error | null;
}

/**
 * Hook for streaming real-time highlighting progress via SSE.
 *
 * Connects to the backend SSE endpoint and receives progress updates
 * as the worker processes a document through the highlighting pipeline.
 *
 * @example
 * const { wordsGenerated, stage, isConnected } = useProgressStream({
 *   assignmentId: "uuid",
 *   documentId: "uuid",
 *   enabled: isAssessing,
 *   onComplete: () => refetchDocument(),
 * });
 *
 * // Display: "Checking thesis clarity... thought for 1,234 words"
 */
export function useProgressStream({
  assignmentId,
  documentId,
  enabled = true,
  onProgress,
  onComplete,
  onError,
}: UseProgressStreamOptions): ProgressStreamState {
  const queryClient = useQueryClient();
  const eventSourceRef = useRef<EventSource | null>(null);
  const isMountedRef = useRef(true);

  // Store callbacks in refs to avoid reconnecting when callbacks change
  const onProgressRef = useRef(onProgress);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);

  // Keep refs up-to-date with latest callbacks
  useLayoutEffect(() => {
    onProgressRef.current = onProgress;
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
  });

  const [state, setState] = useState<ProgressStreamState>({
    isConnected: false,
    wordsGenerated: 0,
    stage: null,
    eventType: null,
    error: null,
  });

  const cleanup = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (isMountedRef.current) {
      setState((prev) => ({ ...prev, isConnected: false }));
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    if (!enabled || !documentId || !assignmentId) {
      cleanup();
      return;
    }

    // Construct SSE URL
    const url = `${API_BASE_URL}/assignments/${assignmentId}/documents/${documentId}/progress/stream`;

    const eventSource = new EventSource(url, {
      withCredentials: true, // Include cookies for auth
    });

    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      if (isMountedRef.current) {
        setState((prev) => ({ ...prev, isConnected: true, error: null }));
      }
    };

    eventSource.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data as string) as unknown;

        // Validate with Zod schema
        const parseResult = ProgressEventSchema.safeParse(data);
        if (!parseResult.success) {
          console.warn("Invalid progress event:", parseResult.error);
          return;
        }

        const progressEvent = parseResult.data;

        if (isMountedRef.current) {
          setState((prev) => ({
            ...prev,
            wordsGenerated: progressEvent.wordsGenerated,
            stage: progressEvent.stage ?? prev.stage,
            eventType: progressEvent.type,
            error:
              progressEvent.type === "failed" || progressEvent.type === "error"
                ? new Error(progressEvent.errorMessage || "Highlighting failed")
                : null,
          }));
        }

        // Call optional callback
        onProgressRef.current?.(progressEvent);

        // Handle terminal events
        if (progressEvent.type === "completed") {
          // Invalidate React Query cache to refetch fresh data
          void queryClient.invalidateQueries({
            queryKey: ["assignment", assignmentId],
          });
          void queryClient.invalidateQueries({
            queryKey: ["assignment-documents", assignmentId],
          });

          onCompleteRef.current?.();
          cleanup();
        } else if (progressEvent.type === "failed" || progressEvent.type === "timeout") {
          const error = new Error(progressEvent.errorMessage || "Highlighting failed");
          onErrorRef.current?.(error);

          // Still invalidate to show failure state
          void queryClient.invalidateQueries({
            queryKey: ["assignment", assignmentId],
          });
          void queryClient.invalidateQueries({
            queryKey: ["assignment-documents", assignmentId],
          });

          cleanup();
        }
      } catch (err) {
        console.error("Failed to parse progress event:", err);
      }
    };

    eventSource.onerror = () => {
      // EventSource auto-reconnects on errors, but we track connection state
      if (isMountedRef.current) {
        setState((prev) => ({
          ...prev,
          isConnected: false,
        }));
      }

      // If the connection was closed (not just a temporary error), clean up
      if (eventSource.readyState === EventSource.CLOSED) {
        const error = new Error("Connection to progress stream closed");
        onErrorRef.current?.(error);
        cleanup();
      }
    };

    // Cleanup on unmount or when dependencies change
    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, [assignmentId, documentId, enabled, queryClient, cleanup]);

  return state;
}
